<template>
  <master-layout>
    <!-- Scanner overlay: shown during active scanning -->
    <div v-if="scanning" class="scanner-overlay">
      <div class="scanner-ui">
        <button class="scanner-close-btn" @click="cancelScan">
          <ion-icon :icon="closeOutline" class="text-[1.75rem]"></ion-icon>
        </button>
        <div class="scanner-frame"></div>
        <p class="scanner-hint">Point your camera at the fotoshare QR code</p>
      </div>
    </div>

    <!-- Loading overlay: shown while claiming photos -->
    <div v-if="claiming" class="scanner-overlay claiming-overlay">
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="text-white text-[1rem] font-semibold">Fetching your photos...</p>
      </div>
    </div>

    <div
      v-show="!scanning && !claiming"
      class="bg-[#F6F4F0] min-h-[calc(100vh-69px)] flex flex-col"
    >
      <div class="px-5 pt-6 pb-0">
        <BackButton :customBack="back" class="mb-4" />
        <h1
          class="font-black text-[2.25rem] leading-[2.375rem] text-[#242425] tracking-[-0.03em] mb-6"
        >
          Receive Photos
        </h1>
      </div>

      <!-- Claim Success -->
      <div v-if="claimedCount > 0" class="flex-1 flex flex-col items-center justify-center px-8">
        <div
          class="w-[64px] h-[64px] bg-[#E8F5E9] rounded-full flex items-center justify-center mb-6"
        >
          <ion-icon :icon="checkmarkCircleOutline" class="text-[2rem] text-[#4CAF50]"></ion-icon>
        </div>
        <h2 class="text-[1.5rem] font-bold leading-[2rem] text-center mb-10">
          {{ claimedCount }} photo{{ claimedCount === 1 ? '' : 's' }} added!
        </h2>
        <button
          class="w-full max-w-[320px] h-[48px] bg-[#3D3A35] text-white rounded-[12px] text-[1rem] font-semibold"
          @click="$router.push('/photodrop')"
        >
          View Photos
        </button>
      </div>

      <!-- Main Content: QR Code + Scanner Button -->
      <div v-else class="flex-1 flex flex-col items-center px-8">
        <!-- QR Code Section -->
        <div v-if="profileLoading" class="pt-12">Loading...</div>
        <template v-else-if="personalId">
          <p class="text-[1rem] leading-[1.5rem] text-[#6B6B6B] text-center mb-8">
            Show this QR code to a photographer to receive your photos
          </p>
          <div class="bg-white rounded-[20px] p-10 flex items-center justify-center">
            <qrcode-vue :value="personalId" :size="230" level="H" />
          </div>
          <p class="text-[1rem] leading-[1.5rem] font-semibold mt-4">
            Personal ID: {{ personalId }}
          </p>
        </template>
        <div v-else class="pt-10 text-center">
          <p class="text-[1rem] text-[#6B6B6B] mb-6">{{ qrErrorMsg }}</p>
          <button
            v-if="!dob || !uin"
            class="h-[48px] px-8 bg-[#3D3A35] text-white rounded-[36px] text-[1rem] font-semibold"
            @click="$router.push('/profile')"
          >
            Edit Profile
          </button>
        </div>

        <!-- Divider -->
        <div class="w-full max-w-[320px] flex items-center gap-4 my-8">
          <div class="flex-1 h-px bg-[#D8DADC]"></div>
          <span class="text-[0.875rem] text-[#6B6B6B] font-medium">or</span>
          <div class="flex-1 h-px bg-[#D8DADC]"></div>
        </div>

        <!-- Open Scanner Button -->
        <button
          v-if="!scanning"
          class="w-full max-w-[320px] h-[48px] bg-[#3D3A35] text-white rounded-[12px] text-[1rem] font-semibold flex items-center justify-center gap-2"
          @click="openScanner"
        >
          <ion-icon :icon="scanOutline" class="text-[1.25rem]"></ion-icon>
          Open Scanner
        </button>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { checkmarkCircleOutline, scanOutline, closeOutline } from 'ionicons/icons';
import QrcodeVue from 'qrcode.vue';
import BackButton from '@/components/common/BackButton.vue';
import { triggerImpact, triggerNotification } from '@/utils/haptics';
import { startScanRaw, stopScan as stopScanner } from '@/utils/qr-scanner';

// The photobooth prints a fotoshare.co claim URL and nothing else, so this is
// both "did the member point the camera at the right screen" and the guard that
// stops an arbitrary scanned URL being handed to the claim action, which posts
// it to our API. Hoisted out of the method because a regex buried mid-flow is
// the kind of thing that gets loosened by someone debugging a single QR.
const FOTOSHARE_URL = /^https:\/\/([\w-]+\.)?fotoshare\.co\//;

export default {
  name: 'ReceivePhotosPage',
  components: { QrcodeVue, BackButton },
  data() {
    return {
      scanning: false,
      claiming: false,
      claimedCount: 0,
    };
  },
  computed: {
    ...mapState('profile', {
      profileLoading: 'loading',
      dob: 'dob',
      uin: 'uin',
      personalId: 'personalId',
    }),
    qrErrorMsg() {
      if (!this.dob && !this.uin) {
        return 'Update your last 4 characters of your NRIC/FIN and Date of Birth under Edit Profile to view your QR code.';
      }
      if (!this.dob) return 'Update your Date of Birth under Edit Profile to view your QR code.';
      if (!this.uin)
        return 'Update your last 4 characters of your NRIC/FIN under Edit Profile to view your QR code.';
      return 'There was an issue generating your Personal ID. Our IT team will be in touch.';
    },
  },
  ionViewWillEnter() {
    this.$store.commit('photobooth/reset');
    this.claimedCount = 0;
  },
  created() {
    this.checkmarkCircleOutline = checkmarkCircleOutline;
    this.scanOutline = scanOutline;
    this.closeOutline = closeOutline;
  },
  ionViewWillLeave() {
    this.closeScanner();
  },
  methods: {
    back() {
      this.$router.go(-1);
    },
    // Named openScanner rather than startScan: inside `methods`, a bare
    // startScan() resolves to the imported util while this.startScan() resolves
    // to the method, and two call sites one keystroke apart that mean different
    // things is a trap for the next editor. Same reason stopScan is imported
    // under the alias stopScanner.
    async openScanner() {
      this.scanning = true;
      try {
        // startScanRaw, not startScan: this page wants the URL string to regex,
        // and parseQrData would hand back a union whose `raw` is absent on the
        // JSON-with-a-type branch. It also owns the permission and platform
        // guards, so their messages arrive here as a thrown error — which is
        // why the button above stays visible when isSupported() is false, where
        // /scanner hides its own. Deliberate: the thrown message already tells
        // the member why nothing happened, and gating the button is a UX change
        // wider than the bug this page is being fixed for (#1226).
        const scannedUrl = await startScanRaw();
        this.scanning = false;

        // The member closed the native scanner without scanning anything.
        if (!scannedUrl) return;

        if (!FOTOSHARE_URL.test(scannedUrl)) {
          triggerNotification('error');
          this.$store.dispatch(
            'toast/error',
            'This QR code is not from fotoshare.co. Please scan the QR code on the photobooth screen.',
          );
          return;
        }

        // Haptic confirmation + show loading
        triggerImpact('medium');
        this.claiming = true;

        await this.$store.dispatch('photobooth/claimFotoshare', scannedUrl);

        if (this.$store.getters['photobooth/hasClaimed']) {
          const count = this.$store.getters['photobooth/photoCount'];
          triggerNotification('success');
          this.claimedCount = count;
          this.$store.commit('photobooth/reset');
        } else if (this.$store.state.photobooth.error) {
          triggerNotification('error');
          this.$store.dispatch('toast/error', this.$store.state.photobooth.error);
          this.$store.commit('photobooth/reset');
        }
      } catch (error) {
        triggerNotification('error');
        this.$store.dispatch('toast/error', error.message || 'Scanner error. Please try again.');
      } finally {
        // The point of the rewrite (#1226): the util's own `finally` restores the
        // native background and drops the scanner-active classes, so nothing
        // about the transparent webview is left for this page to forget. All
        // that survives here is the page's own flags — and `claiming` is in the
        // `finally` because a throw from the middle of the claim used to strand
        // the member on the "Fetching your photos..." spinner for ever.
        this.scanning = false;
        this.claiming = false;
      }
    },
    async cancelScan() {
      await stopScanner();
      this.scanning = false;
    },
    closeScanner() {
      // Unconditional, where this used to read `if (this.scanning)`. That guard
      // was the second half of #1226: the catch above had already cleared
      // `scanning`, so after any failed scan the native scanner view was never
      // torn down and leaving the page could no longer tear it down either. The
      // util's stopScan already tolerates "not scanning" — it swallows the
      // plugin's rejection and restores the background regardless — so there is
      // nothing for a guard to protect.
      return stopScanner();
    },
  },
};
</script>

<style>
/* Scanner transparency: make all layers see-through for native camera */
body.scanner-active,
body.scanner-active ion-app,
body.scanner-active ion-app ion-router-outlet,
body.scanner-active ion-page,
body.scanner-active ion-content,
body.scanner-active ion-content::part(background),
body.scanner-active .scroll-content {
  --background: transparent !important;
  --ion-background-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;
}

ion-app.scanner-active {
  --ion-background-color: transparent !important;
}

/* Scanner overlay UI */
.scanner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.claiming-overlay {
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
}

.scanner-ui {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.scanner-close-btn {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 16px);
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.scanner-frame {
  width: 250px;
  height: 250px;
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 20px;
}

.scanner-hint {
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  padding: 0 32px;
}
</style>
